import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from './icons';
import type { BottomNavProps, TabKey } from '../types';
import { t } from '../i18n';
import { colors, gradients, layout, radius, shadow, spacing, typography } from '../theme/tokens';

export type TabIcon = keyof typeof Ionicons.glyphMap;

// L'ORDRE FAIT LA BARRE, et il fait aussi la route d'atterrissage : TabNavigator ne pose aucun
// `initialRouteName`, donc le premier onglet est celui qu'on voit en arrivant. Carrière est en tête
// parce qu'elle a remplacé l'accueil, pas par esthétique — déplacer cette ligne change l'écran
// d'après connexion sans qu'aucun test de navigation ne l'annonce.
//
// QUATRE entrées, deux avant le FAB et deux après (cf. les `slice` plus bas) : passer à trois ou
// cinq décentrerait le bouton étincelle.
export const tabs = (): { key: TabKey; label: string; icon: TabIcon }[] => [
  // L'onglet regroupe CV, LinkedIn, lettre de motivation et entretien IA : « Carrière » nomme la
  // destination, pas l'un de ses contenus. La CLÉ reste 'cvs' — elle mappe la route de
  // navigation, que renommer casserait les liens impératifs, non typés.
  { key: 'cvs', label: t('nav.carriere'), icon: 'document-text' },
  { key: 'offers', label: t('nav.offers'), icon: 'briefcase' },
  { key: 'news', label: t('nav.news'), icon: 'newspaper' },
  { key: 'profile', label: t('nav.profile'), icon: 'person-circle' },
];

export function BottomNav({ activeKey, onTabPress, onFabPress, testID }: BottomNavProps) {
  // Construit AU RENDU : une constante de module figerait les libellés dans la langue chargée
  // au premier import, et le changement de langue ne les rattraperait pas.
  const TABS = tabs();

  /**
   * INSET BAS DE L'APPAREIL, ajouté au rembourrage de la barre.
   *
   * La barre est l'élément le plus bas de l'application — react-navigation la rend en frère du
   * conteneur d'écrans — et personne d'autre ne consomme cet inset : la SafeAreaView des écrans
   * n'ouvre que le bord haut. Sans cette ligne, la zone de l'indicateur d'accueil recouvrait le
   * bas de la barre sur tous les appareils qui en ont un, et le dernier bloc de chaque onglet
   * paraissait collé au bord.
   *
   * `spacing.lg` reste le PLANCHER : sur un appareil sans indicateur (iPhone SE, inset = 0), la
   * barre garde exactement le rembourrage qu'elle avait.
   */
  const insets = useSafeAreaInsets();

  return (
    /* DEUX NIVEAUX, ET LA DISTINCTION EST TOUTE LA RÉPONSE AU GRAND ÉCRAN.
       La SURFACE (fond, filet supérieur, insets) tient toute la largeur de la fenêtre : c'est ce
       qui ancre la barre au bas de l'écran au lieu de la faire flotter au milieu. La RANGÉE, elle,
       est bornée et centrée — quatre onglets et un bouton répartis sur 1200 px seraient épars.
       Sur mobile la borne est inerte (430 pt au maximum), la rangée occupe 100 % comme avant, et
       la géométrie de la barre est rigoureusement celle d'hier : mêmes rembourrages, aux mêmes
       éléments du modèle de boîte. */
    <View testID={testID} style={[styles.bar, { paddingBottom: BAR_PADDING_BOTTOM + insets.bottom }]}>
      <View style={styles.row} className="flex-row items-center justify-around">
        {TABS.slice(0, 2).map((tab) => (
          <TabItem key={tab.key} tab={tab} active={tab.key === activeKey} onPress={onTabPress} />
        ))}

        <Pressable
          testID="nav-fab"
          accessibilityRole="button"
          accessibilityLabel={t('nav.aiAction')}
          onPress={onFabPress}
          style={styles.fab}
        >
          <LinearGradient
            testID="nav-fab-gradient"
            colors={gradients.brand}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.fabGradient}
          >
            <Ionicons name="sparkles" size={26} color={colors.white} />
          </LinearGradient>
        </Pressable>

        {TABS.slice(2).map((tab) => (
          <TabItem key={tab.key} tab={tab} active={tab.key === activeKey} onPress={onTabPress} />
        ))}
      </View>
    </View>
  );
}

function TabItem({
  tab,
  active,
  onPress,
}: {
  tab: { key: TabKey; label: string; icon: TabIcon };
  active: boolean;
  onPress: (key: TabKey) => void;
}) {
  const color = active ? colors.electricDeep : colors.grey500;
  return (
    <Pressable
      testID={`nav-${tab.key}`}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      onPress={() => onPress(tab.key)}
      style={styles.tab}
    >
      <Ionicons name={tab.icon} size={24} color={color} style={styles.icon} />
      <Text testID={`nav-${tab.key}-label`} style={[styles.label, { color }]}>
        {tab.label}
      </Text>
      {/* Slot de hauteur fixe → pas de décalage entre onglet actif/inactif. */}
      <View style={styles.underlineSlot}>
        {active ? (
          <LinearGradient
            testID={`nav-${tab.key}-underline`}
            colors={gradients.brand}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.underline}
          />
        ) : null}
      </View>
    </Pressable>
  );
}

/** Rembourrage bas de la barre AVANT l'inset de l'appareil. */
export const BAR_PADDING_BOTTOM = spacing.lg;
/** Hauteur dont le bouton central dépasse au-dessus de la barre : ce qui se pose en bas d'un écran doit la laisser libre. */
export const DEPASSEMENT_FAB = spacing.xl;

const styles = StyleSheet.create({
  // SURFACE : ce qui doit toucher les deux bords de la fenêtre. Le fond et le filet supérieur y
  // restent, ainsi que les rembourrages VERTICAUX — déplacer ces derniers dans la rangée
  // changerait la hauteur de la barre sur mobile.
  bar: {
    backgroundColor: colors.white,
    paddingTop: spacing.sm,
    // paddingBottom appliqué au rendu : il dépend de l'inset (voir BAR_PADDING_BOTTOM).
    borderTopWidth: 1,
    borderTopColor: colors.grey200,
  },
  // RANGÉE : la disposition d'hier, à l'identique, plus la borne. `width: '100%'` la fait occuper
  // toute la surface tant que la fenêtre est plus étroite que la borne — donc toujours, sur mobile.
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: spacing.sm,
    width: '100%',
    maxWidth: layout.tabBarMaxWidth,
    alignSelf: 'center',
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    marginBottom: spacing.xs / 2,
  },
  label: {
    fontSize: typography.caption.size,
    fontWeight: typography.caption.weight,
    letterSpacing: typography.caption.letterSpacing,
  },
  underlineSlot: {
    height: 3,
    marginTop: spacing.xs / 2,
    justifyContent: 'center',
  },
  underline: {
    width: 22,
    height: 3,
    borderRadius: radius.pill,
  },
  // Anneau blanc (padding) autour du disque dégradé + surélévation par rapport à la barre.
  fab: {
    borderRadius: radius.pill,
    backgroundColor: colors.white,
    padding: 3,
    marginHorizontal: spacing.sm,
    marginTop: -DEPASSEMENT_FAB,
    ...shadow.floating,
  },
  fabGradient: {
    width: 56,
    height: 56,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
